'use client';

import { FC, ComponentProps } from 'react';
import { EntryFields, Entry } from 'contentful';
import {
  TypeBackgroundColorFields,
  TypeBannerTextFields,
  TypeLink_externalFields,
  TypeLink_internalFields,
} from '@/core/generated-types';
import useCurrentPromo, { PromoOverrideProps } from '@/hooks/useCurrentPromo';
import { MessageBanner } from '@/components/nav-v2/GlobalNav/MessageBanner/MessageBanner';
import { normalizeToRichText, colorStringToEntry } from './utils';

/** Banner-specific props - subset of TypeGlobalNavFields used by MessageBanner */
type MessageBannerProps = ComponentProps<typeof MessageBanner>;

export interface PromoSlotSitewideBannerProps extends PromoOverrideProps {
  bannerText?: EntryFields.RichText;
  bannerTextList?: Entry<TypeBannerTextFields>[];
  bannerLinks?: Entry<TypeLink_externalFields | TypeLink_internalFields>[];
  bannerBackgroundColor?: Entry<TypeBackgroundColorFields>;
  bannerTextColor?: Entry<TypeBackgroundColorFields>;
  enableAccessibilityLabel?: EntryFields.Symbol;
  showAccessibilityLink?: EntryFields.Boolean;
  containerWidth?: EntryFields.Symbol;
}

/**
 * PromoSlotSitewideBanner - Sitewide banner with Contentful fallback.
 *
 * A drop-in replacement for MessageBanner that prioritizes promotion data
 * and falls back to Contentful banner content when no promotion is available.
 *
 * Priority:
 * 1. Promotion sitewide message - fetched first
 * 2. Contentful banner (bannerText) - fallback when no promotion
 * 3. Nothing - returns null
 */
const PromoSlotSitewideBanner: FC<PromoSlotSitewideBannerProps> = ({
  bannerText,
  bannerTextList,
  bannerLinks,
  bannerBackgroundColor,
  bannerTextColor,
  showAccessibilityLink,
  enableAccessibilityLabel,
  containerWidth,
  ...override
}) => {
  // Always fetch promotion first (priority)
  const { promotion } = useCurrentPromo({
    ...override,
    disabled: override.disabled,
  });

  // Check if promotion has sitewide banner message content
  const siteWideBannerMessage = normalizeToRichText(
    promotion?.fields?.siteWideBannerMessage,
  );

  // Promotion takes priority - render with promotion data if available
  if (siteWideBannerMessage) {
    // Convert promotion color strings to the Entry format MessageBanner expects
    // white and blue-deep are the default colors when undefined (existing behavior)
    const promoBgColor = colorStringToEntry(
      promotion?.fields?.siteWideBannerBackgroundColor,
    );
    const promoTextColor = colorStringToEntry(
      promotion?.fields?.siteWideBannerTextColor,
    );

    // Render MessageBanner with promotion data (bannerLinks passed through from props)
    return (
      <MessageBanner
        {...({
          bannerText: siteWideBannerMessage as EntryFields.RichText,
          bannerTextList,
          bannerLinks,
          bannerBackgroundColor: promoBgColor,
          bannerTextColor: promoTextColor,
          showAccessibilityLink,
          enableAccessibilityLabel,
          containerWidth,
        } as MessageBannerProps)}
      />
    );
  }

  // Fallback to Contentful banner if no promotion
  const hasContentfulBanner = bannerText && bannerText.content.length > 0;

  if (!hasContentfulBanner) return null;

  // Render MessageBanner with Contentful props
  return (
    <MessageBanner
      {...({
        bannerText,
        bannerTextList,
        bannerLinks,
        bannerBackgroundColor,
        bannerTextColor,
        showAccessibilityLink,
        enableAccessibilityLabel,
        containerWidth,
      } as MessageBannerProps)}
    />
  );
};

export default PromoSlotSitewideBanner;
